import {
  BrandReportTimespan,
  type MonitorMediaFilterType,
  UserLocale,
} from '@mnd-frontend/graphql-schema';
import { createContext, useContext } from 'react';
import type { Report2MenuSectionFragment } from '../../../generated/types';

type Props = {
  id: string;
  pressroomId: string;
  companyName: string;
  brand: string;
  locale: UserLocale;
  compareToPreviousYear: boolean;
  mediaTypes: MonitorMediaFilterType[];
  timespan: BrandReportTimespan;
  searchProfiles: {
    id: string;
    name: string;
  }[];
  competitorSearchProfiles: {
    ids: string[];
    name: string;
  }[];
  menuItems: Report2MenuSectionFragment['sections'];
  excludedSourceGroups: {
    id: string;
    name: string;
  }[];
};

const defaultValue = {
  id: '',
  pressroomId: '',
  companyName: '',
  brand: '',
  locale: UserLocale.En,
  compareToPreviousYear: false,
  mediaTypes: [],
  fromDate: new Date(),
  toDate: new Date(),
  timespan: BrandReportTimespan.LastMonth,
  searchProfiles: [],
  competitorSearchProfiles: [],
  menuItems: [],
  excludedSourceGroups: [],
};

const ReportStaticDataContext = createContext<Props & { fromDate: Date; toDate: Date }>(
  defaultValue,
);

export const ReportStaticDataContextProvider = ({
  children,
  id,
  pressroomId,
  companyName,
  locale,
  brand,
  compareToPreviousYear,
  mediaTypes,
  fromDate,
  toDate,
  timespan,
  searchProfiles,
  competitorSearchProfiles,
  menuItems,
  excludedSourceGroups,
}: Props & { fromDate: string; toDate: string } & {
  children: React.ReactNode;
}) => {
  return (
    <ReportStaticDataContext.Provider
      value={{
        id,
        pressroomId,
        companyName,
        locale,
        brand,
        compareToPreviousYear,
        mediaTypes,
        fromDate: new Date(fromDate),
        toDate: new Date(toDate),
        timespan,
        searchProfiles,
        competitorSearchProfiles,
        menuItems,
        excludedSourceGroups,
      }}
    >
      {children}
    </ReportStaticDataContext.Provider>
  );
};

export const useReportStaticData = () => {
  const context = useContext(ReportStaticDataContext);
  if (context === defaultValue) {
    throw new Error('useReportStaticData must be used within a ReportStaticDataContextProvider');
  }
  return context;
};
